import { mapObjIndexed } from 'ramda';
import { JSONSchema } from '@invent/json-settings';
import { WidgetType } from './constants';
import type {
  IWidget,
  IWidgetMetadata,
  NavGraphNodeT,
  NavGraphT,
  IPortalSettingsStore,
  IPlatformWidget,
  IndexedPlatformWidgets
} from './types';

const isObject = (obj: any) => {
  return Object.prototype.toString.call(obj) === '[object Object]';
};

const emptyObject = {};
const emptyJSONSettingsObject: JSONSchema = { jsonSchema: {}, uiSchema: {}, extraErrors: {} };

function enrichWidget<T extends IWidget = IWidget>(widget: T): T {
  const settingsSchema = widget.settingsSchema ?? emptyJSONSettingsObject;
  settingsSchema.jsonSchema = settingsSchema.jsonSchema ?? emptyObject;
  settingsSchema.uiSchema = settingsSchema.uiSchema ?? emptyObject;
  settingsSchema.extraErrors = settingsSchema.extraErrors ?? emptyObject;
  return { ...widget, settingsSchema };
}

// We rely on agreement that any api url props contains "Url" substring
export const injectApiUrl = (
  defaultProps: Record<string, unknown> = {}
): Record<string, unknown> => {
  const result = mapObjIndexed((value, key) => {
    if (Array.isArray(value)) {
      return value.map((item) => (isObject(item) ? injectApiUrl(item) : item));
    }
    if (isObject(value)) {
      return injectApiUrl(value as Record<string, unknown>);
    }
    if (!key.includes('Url') && !key.includes('url')) {
      return value;
    }
    const stringValue = String(value);
    return stringValue.includes('_internal_api_')
      ? stringValue.replace('_internal_api_', window.API_BASE_URL)
      : value;
  }, defaultProps);

  return result;
};

export function fromServerWidgetsToInternalFormat(
  data: IWidgetMetadata[]
): Record<string, IWidgetMetadata> {
  return data.reduce(
    (acc, { defaultProps, widgetId, ...rest }) => {
      const widget = {
        ...rest,
        widgetId,
        defaultProps: injectApiUrl(defaultProps)
      };
      acc[widgetId] = enrichWidget(widget);
      return acc;
    },
    {} as Record<string, IWidgetMetadata>
  );
}

export const indexPlatformWidgets = (data: IPlatformWidget[]) =>
  data.reduce(
    (acc, current) => {
      const { domain, placement } = current;
      if (!acc.byDomain[domain]) {
        acc.byDomain[domain] = {};
      }
      const widget = enrichWidget({
        ...current,
        defaultProps: injectApiUrl(current?.defaultProps)
      });
      acc.byDomain[domain][placement] = widget.widgetId;
      acc.byId[widget.widgetId] = widget;
      return acc;
    },
    { byId: {}, byDomain: {} } as {
      byId: Record<string, IPlatformWidget>;
      byDomain: IndexedPlatformWidgets;
    }
  );

export function remapWidgetStoreResponse(overrides: Record<number, IWidgetMetadata>) {
  return (
    acc: {
      platform: IPlatformWidget[];
      dashboard: IWidgetMetadata[];
    },
    current: IWidgetMetadata
  ) => {
    const forOverride = overrides[current.widgetId];
    const widget = forOverride ? { ...current, ...forOverride } : current;
    if (widget.widgetType === WidgetType.Universal) {
      acc.platform.push(widget);
      acc.dashboard.push(widget);
      return acc;
    }
    if (widget.widgetType === WidgetType.Platform) {
      acc.platform.push(widget);
    } else {
      acc.dashboard.push(widget);
    }
    return acc;
  };
}

export function generateFlatNavGraph(navgraph: IPortalSettingsStore['navgraph']) {
  const system: NavGraphT = navgraph.default ?? {};
  const personal: NavGraphT = navgraph.personal?.personal?.children ?? {};

  function flatten(node: NavGraphT, acc: Map<string, NavGraphNodeT>, prefix: string[]) {
    const entries = Object.entries(node);

    for (const [url, board] of entries) {
      if (url === 'order') {
        continue;
      }

      const path = [...prefix, url];

      flatten(board.children ?? board.tabs ?? {}, acc, path);

      if (board.role || board.tabs) {
        acc.set(path.join('/'), {
          role: board.role,
          title: board.title,
          tabs: board.tabs
        });
      }
    }

    return acc;
  }

  return {
    system: flatten(system, new Map(), []),
    personal: flatten(personal, new Map(), [])
  };
}
